/**
 * Video explanation helpers for the test-results feature.
 *
 * The admin enters one base video URL for the whole test and, for each
 * question, an optional time string (mm:ss or hh:mm:ss). This module
 * converts that time to total seconds and builds a timestamped link
 * that opens in a new browser tab.
 *
 * Nothing here embeds a player, autoplays, or loads third-party video
 * content before the user clicks — the link is a plain anchor.
 */

/**
 * Parse a user-entered time string into total seconds.
 *
 * Accepted:
 *   "4:45"      -> 285
 *   "04:45"     -> 285
 *   "12:10"     -> 730
 *   "1:04:45"   -> 3885
 *   ""          -> null (means "no timestamp for this question")
 *   "   "       -> null
 *
 * Rejected (returns { seconds: null, error: <sk message> }):
 *   "abc", "5", "1:2:3:4", "-1:00", "12:60", negative values,
 *   fractional seconds, leading/trailing garbage.
 *
 * @param {string} input raw admin input
 * @returns {{ seconds: number|null, error: string|null }}
 */
export function parseTimeToSeconds(input) {
  if (input === null || input === undefined) {
    return { seconds: null, error: null }
  }
  const raw = String(input).trim()
  if (raw === '') {
    return { seconds: null, error: null }
  }

  // Strict mm:ss or hh:mm:ss — each field is digits only.
  const match = raw.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/)
  if (!match) {
    return {
      seconds: null,
      error: 'Neplatný formát času. Použi mm:ss (napr. 04:45) alebo hh:mm:ss (napr. 1:04:45).',
    }
  }

  let hours = 0
  let minutes = 0
  let seconds = 0
  if (match[3] !== undefined) {
    hours = parseInt(match[1], 10)
    minutes = parseInt(match[2], 10)
    seconds = parseInt(match[3], 10)
  } else {
    minutes = parseInt(match[1], 10)
    seconds = parseInt(match[2], 10)
  }

  if (minutes > 59 || seconds > 59) {
    return {
      seconds: null,
      error: 'Minúty a sekundy musia byť medzi 0 a 59.',
    }
  }

  const total = hours * 3600 + minutes * 60 + seconds
  if (total < 0) {
    return { seconds: null, error: 'Čas nemôže byť záporný.' }
  }
  return { seconds: total, error: null }
}

/**
 * Format a seconds count for display back into a form field.
 * Inverse of parseTimeToSeconds; used when loading an existing question.
 *
 *   0        -> "0:00"
 *   285      -> "4:45"
 *   730      -> "12:10"
 *   3885     -> "1:04:45"
 *   null     -> ""
 *   -1       -> "" (defensive)
 */
export function secondsToDisplay(totalSeconds) {
  if (totalSeconds === null || totalSeconds === undefined) return ''
  const n = Number(totalSeconds)
  if (!Number.isFinite(n) || n < 0) return ''
  const h = Math.floor(n / 3600)
  const m = Math.floor((n % 3600) / 60)
  const s = n % 60
  const pad = (x) => String(x).padStart(2, '0')
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`
  return `${m}:${pad(s)}`
}

/**
 * Validate an http/https URL. Rejects anything else including
 * javascript:, data:, file:, mailto:, etc.
 */
export function isValidHttpUrl(candidate) {
  if (!candidate) return false
  const raw = String(candidate).trim()
  if (raw === '') return false
  let parsed
  try {
    parsed = new URL(raw)
  } catch (_err) {
    return false
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:'
}

/**
 * Recognise a YouTube video URL. Accepts:
 *   - youtube.com/watch?v=ID       (with or without www)
 *   - youtu.be/ID                  (short form)
 *
 * Returns:
 *   { platform: 'youtube', form: 'watch' | 'short', videoId: string }
 * or null.
 */
export function detectVideoPlatform(url) {
  if (!isValidHttpUrl(url)) return null
  let parsed
  try {
    parsed = new URL(url)
  } catch (_err) {
    return null
  }
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '')
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    if (parsed.pathname === '/watch') {
      const v = parsed.searchParams.get('v')
      if (v) return { platform: 'youtube', form: 'watch', videoId: v }
    }
  }
  if (host === 'youtu.be') {
    const id = parsed.pathname.replace(/^\//, '').split('/')[0]
    if (id) return { platform: 'youtube', form: 'short', videoId: id }
  }
  return null
}

/**
 * Take a base video URL and a start-time in seconds and return a URL
 * that starts playback at that second, preserving any existing query
 * string on the input. Never returns a malformed URL — on any parse
 * failure or on isValidHttpUrl==false it returns null so the caller
 * can decide not to render the link.
 *
 * Rules:
 *   - youtube.com/watch?v=ID           -> ...&t=<seconds>s
 *   - youtu.be/ID                      -> ...?t=<seconds>
 *   - other valid http/https URL       -> append ?t=<seconds> or &t=<seconds>
 *     (fall-through; user is warned that non-YouTube may not honour it)
 *
 *   startSeconds falsy or 0            -> return original URL untouched
 *     (i.e. "whole test video from the beginning" uses the base URL)
 */
export function buildVideoUrlWithTimestamp(baseUrl, startSeconds) {
  if (!isValidHttpUrl(baseUrl)) return null
  const seconds = Number(startSeconds)
  if (!Number.isFinite(seconds) || seconds <= 0) {
    // No timestamp requested — return the original URL.
    return baseUrl
  }
  let parsed
  try {
    parsed = new URL(baseUrl)
  } catch (_err) {
    return null
  }
  const platform = detectVideoPlatform(baseUrl)
  // YouTube canonical param is `t`; for the short form we use bare
  // seconds (per the user's example), for watch form we use `<n>s`.
  if (platform && platform.platform === 'youtube') {
    if (platform.form === 'short') {
      parsed.searchParams.set('t', String(Math.floor(seconds)))
    } else {
      parsed.searchParams.set('t', `${Math.floor(seconds)}s`)
    }
  } else {
    // Generic fall-through: use bare `t=<seconds>`.
    parsed.searchParams.set('t', String(Math.floor(seconds)))
  }
  return parsed.toString()
}
